import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "partialOptions",
    "firstDurationSelect",
    "lastDurationSelect",
    "isPartialFirst",
    "isPartialLast",
    "firstDayFields",
    "lastDayFields",
    "firstHalfHint",
    "lastHalfHint",
    "lastDaySection",
    "hoursFirstDayInput",
    "hoursLastDayInput",
    "calculatedHoursFirst",
    "calculatedHoursLast",
    "timeFromFirstDay",
    "timeToFirstDay",
    "timeFromLastDay",
    "timeToLastDay",
    "timeErrorFirst",
    "timeErrorLast",
    "waivedSection",
    "waivedCheckbox",
  ]
  static values = {
    allowsPartial: Boolean,
    allowsHalf: Boolean,
    timeErrorMessage: { type: String, default: "End time must be after start time" },
    continuationUrl: String,
    continuationExcludeId: String,
  }

  connect() {
    this.buildSickLeaveTypeMap()
    this.syncSelectedType()
    this.applyTypePermissions()
    this.updateVisibility()
    this.updateLastDayVisibility()
  }

  // Which earlier sick notes qualify as the illness this one continues depends on
  // employee, type and start date, so the frame is reloaded whenever they change.
  refreshContinuationOptions() {
    if (!this.hasContinuationUrlValue) return
    const frame = this.element.querySelector("#sick_note_continuation")
    if (!frame) return

    const employeeIds = this.selectedEmployeeIds()
    const params = new URLSearchParams()
    // A continuation targets one specific earlier note, so it is offered only
    // when exactly one employee is selected.
    if (employeeIds.length === 1) params.set("employee_id", employeeIds[0])
    params.set("sick_leave_type_id", this.fieldValue('[name*="sick_leave_type_id"]'))
    params.set("date_from", this.fieldValue('[name*="[date_from]"]'))
    if (this.continuationExcludeIdValue) params.set("exclude_id", this.continuationExcludeIdValue)
    // Carry the current pick so a refresh that still offers it keeps it selected.
    // An explicitly picked blank ("Nicht verknüpfen") travels as the 'none'
    // sentinel — indistinguishable from an untouched picker by value alone, and
    // without it the reload would re-preselect the auto-link default the admin
    // just deselected.
    const selected = this.fieldValue('[name="sick_note[predecessor_sick_note_id]"]')
    if (selected) params.set("predecessor_sick_note_id", selected)
    else if (this.continuationPickedBlank) params.set("predecessor_sick_note_id", "none")

    // Assigning a NEW src makes Turbo fetch on its own; reload() is only needed
    // when the src is unchanged (e.g. the user re-picked the same value).
    const src = `${this.continuationUrlValue}?${params.toString()}`
    if (frame.getAttribute("src") === src) {
      frame.reload()
    } else {
      frame.src = src
    }
  }

  selectedEmployeeIds() {
    const multi = this.element.querySelector('[name="sick_note[employee_ids][]"]')
    if (multi) {
      return Array.from(this.element.querySelectorAll('[name="sick_note[employee_ids][]"]'))
        .flatMap((el) =>
          el.multiple ? Array.from(el.selectedOptions).map((o) => o.value) : [el.value],
        )
        .filter((value) => value)
    }
    const single = this.fieldValue('[name*="[employee_id]"]')
    return single ? [single] : []
  }

  fieldValue(selector) {
    const field = this.element.querySelector(selector)
    return field ? field.value : ""
  }

  buildSickLeaveTypeMap() {
    this.sickLeaveTypeMap = {}
    const dataElements = this.element.querySelectorAll("[data-sick-leave-type-id]")
    dataElements.forEach((el) => {
      this.sickLeaveTypeMap[el.dataset.sickLeaveTypeId] = {
        partial: el.dataset.allowsPartial === "true",
        half: el.dataset.allowsHalf === "true",
        waitingPeriod: el.dataset.waitingPeriodActive === "true",
      }
    })
  }

  syncSelectedType() {
    const select = this.element.querySelector('[name*="sick_leave_type_id"]')
    const selectedId = select ? select.value : null
    const perms = (this.sickLeaveTypeMap && this.sickLeaveTypeMap[selectedId]) || {}
    this.allowsPartialValue = perms.partial === true
    this.allowsHalfValue = perms.half === true
    this.waitingPeriodActive = perms.waitingPeriod === true
  }

  sickLeaveTypeChanged() {
    this.syncSelectedType()
    this.applyTypePermissions()
    this.updateVisibility()
    this.refreshContinuationOptions()
  }

  dateChanged() {
    this.updateLastDayVisibility()
  }

  // Only the START date changes which earlier notes qualify as a continuation —
  // refreshing on the end date too would discard the admin's pick for nothing.
  startDateChanged() {
    this.updateLastDayVisibility()
    this.refreshContinuationOptions()
  }

  employeeChanged() {
    this.refreshContinuationOptions()
  }

  // Fired by the picker inside the continuation frame. Only an explicit blank
  // pick is remembered — see refreshContinuationOptions.
  continuationPicked(event) {
    this.continuationPickedBlank = event.target.value === ""
  }

  applyTypePermissions() {
    ;[this.optionalTarget("firstDurationSelect"), this.optionalTarget("lastDurationSelect")]
      .filter((select) => select)
      .forEach((select) => {
        Array.from(select.options).forEach((option) => {
          const requires = option.dataset.requires
          let allowed = true
          if (requires === "partial") allowed = this.allowsPartialValue
          if (requires === "half") allowed = this.allowsHalfValue
          option.disabled = !allowed
          option.hidden = !allowed
        })
        const current = select.options[select.selectedIndex]
        if (current && (current.disabled || current.hidden)) {
          select.value = "full_day"
        }
      })
    this.firstDurationChanged()
    this.lastDurationChanged()
  }

  firstDurationChanged() {
    this.applyDuration("first")
  }

  lastDurationChanged() {
    this.applyDuration("last")
  }

  applyDuration(slot) {
    const select = this.optionalTarget(`${slot}DurationSelect`)
    if (!select) return

    const value = select.value
    const isHours = value === "hours"
    const isHalf = value.startsWith("half_day")
    const isPartial = value !== "full_day"

    const hiddenPartial = this.optionalTarget(slot === "first" ? "isPartialFirst" : "isPartialLast")
    if (hiddenPartial) hiddenPartial.value = isPartial ? "1" : "0"

    const fields = this.optionalTarget(`${slot}DayFields`)
    if (fields) fields.classList.toggle("hidden", !isHours)

    const hint = this.optionalTarget(`${slot}HalfHint`)
    if (hint) hint.classList.toggle("hidden", !isHalf)

    if (!isHours) this.clearTimeInputs(slot)
  }

  // The duration block is only rendered when the tenant has configurable sick
  // leave types (the legacy type_of_sickness form has none), and Stimulus'
  // `*Target` getters throw on a missing element — which aborted connect().
  optionalTarget(name) {
    return this[`has${this.capitalize(name)}Target`] ? this[`${name}Target`] : null
  }

  clearTimeInputs(slot) {
    const hoursInput = slot === "first" ? "hoursFirstDayInput" : "hoursLastDayInput"
    const timeFrom = slot === "first" ? "timeFromFirstDay" : "timeFromLastDay"
    const timeTo = slot === "first" ? "timeToFirstDay" : "timeToLastDay"
    const calc = slot === "first" ? "calculatedHoursFirst" : "calculatedHoursLast"
    const error = slot === "first" ? "timeErrorFirst" : "timeErrorLast"

    if (this[`has${this.capitalize(hoursInput)}Target`]) this[`${hoursInput}Target`].value = ""
    if (this[`has${this.capitalize(timeFrom)}Target`]) this[`${timeFrom}Target`].value = ""
    if (this[`has${this.capitalize(timeTo)}Target`]) this[`${timeTo}Target`].value = ""
    if (this[`has${this.capitalize(calc)}Target`]) this[`${calc}Target`].classList.add("hidden")
    if (this[`has${this.capitalize(error)}Target`]) this[`${error}Target`].classList.add("hidden")
  }

  capitalize(name) {
    return name.charAt(0).toUpperCase() + name.slice(1)
  }

  calculateHoursDifference(timeFrom, timeTo) {
    if (!timeFrom || !timeTo) return null

    const [fromHours, fromMinutes] = timeFrom.split(":").map(Number)
    const [toHours, toMinutes] = timeTo.split(":").map(Number)

    if (isNaN(fromHours) || isNaN(fromMinutes) || isNaN(toHours) || isNaN(toMinutes)) return null

    const fromTotal = fromHours * 60 + fromMinutes
    const toTotal = toHours * 60 + toMinutes

    if (toTotal <= fromTotal) return null // End must be after start

    const diffMinutes = toTotal - fromTotal
    return Math.round((diffMinutes / 60) * 4) / 4 // Round to nearest 0.25
  }

  updateFirstDayHours() {
    if (!this.hasTimeFromFirstDayTarget || !this.hasTimeToFirstDayTarget) return

    const timeFrom = this.timeFromFirstDayTarget.value
    const timeTo = this.timeToFirstDayTarget.value
    const hours = this.calculateHoursDifference(timeFrom, timeTo)

    if (hours !== null) {
      if (this.hasHoursFirstDayInputTarget) this.hoursFirstDayInputTarget.value = hours
      if (this.hasCalculatedHoursFirstTarget) {
        this.calculatedHoursFirstTarget.textContent = `${hours} h`
        this.calculatedHoursFirstTarget.classList.remove("hidden")
      }
      if (this.hasTimeErrorFirstTarget) this.timeErrorFirstTarget.classList.add("hidden")
    } else if (timeFrom && timeTo) {
      if (this.hasTimeErrorFirstTarget) {
        this.timeErrorFirstTarget.textContent = this.timeErrorMessageValue
        this.timeErrorFirstTarget.classList.remove("hidden")
      }
      if (this.hasHoursFirstDayInputTarget) this.hoursFirstDayInputTarget.value = ""
      if (this.hasCalculatedHoursFirstTarget)
        this.calculatedHoursFirstTarget.classList.add("hidden")
    } else {
      if (this.hasCalculatedHoursFirstTarget)
        this.calculatedHoursFirstTarget.classList.add("hidden")
      if (this.hasTimeErrorFirstTarget) this.timeErrorFirstTarget.classList.add("hidden")
    }
  }

  updateLastDayHours() {
    if (!this.hasTimeFromLastDayTarget || !this.hasTimeToLastDayTarget) return

    const timeFrom = this.timeFromLastDayTarget.value
    const timeTo = this.timeToLastDayTarget.value
    const hours = this.calculateHoursDifference(timeFrom, timeTo)

    if (hours !== null) {
      if (this.hasHoursLastDayInputTarget) this.hoursLastDayInputTarget.value = hours
      if (this.hasCalculatedHoursLastTarget) {
        this.calculatedHoursLastTarget.textContent = `${hours} h`
        this.calculatedHoursLastTarget.classList.remove("hidden")
      }
      if (this.hasTimeErrorLastTarget) this.timeErrorLastTarget.classList.add("hidden")
    } else if (timeFrom && timeTo) {
      if (this.hasTimeErrorLastTarget) {
        this.timeErrorLastTarget.textContent = this.timeErrorMessageValue
        this.timeErrorLastTarget.classList.remove("hidden")
      }
      if (this.hasHoursLastDayInputTarget) this.hoursLastDayInputTarget.value = ""
      if (this.hasCalculatedHoursLastTarget) this.calculatedHoursLastTarget.classList.add("hidden")
    } else {
      if (this.hasCalculatedHoursLastTarget) this.calculatedHoursLastTarget.classList.add("hidden")
      if (this.hasTimeErrorLastTarget) this.timeErrorLastTarget.classList.add("hidden")
    }
  }

  updateVisibility() {
    if (this.hasPartialOptionsTarget) {
      const show = this.allowsPartialValue || this.allowsHalfValue
      this.partialOptionsTarget.classList.toggle("hidden", !show)
    }
    // Only types with a Wartefrist have a Karenz to waive. Untick on hide, or a
    // stale tick would still submit from the hidden section after switching to a
    // type without one.
    if (this.hasWaivedSectionTarget) {
      this.waivedSectionTarget.classList.toggle("hidden", !this.waitingPeriodActive)
      if (!this.waitingPeriodActive && this.hasWaivedCheckboxTarget) {
        this.waivedCheckboxTarget.checked = false
      }
    }
    this.updateLastDayVisibility()
  }

  updateLastDayVisibility() {
    if (!this.hasLastDaySectionTarget) return

    const dateFrom = this.element.querySelector('[name*="date_from"]')?.value
    const dateTo = this.element.querySelector('[name*="date_to"]')?.value
    const isMultiDay = dateFrom && dateTo && dateFrom !== dateTo
    this.lastDaySectionTarget.classList.toggle("hidden", !isMultiDay)

    if (!isMultiDay && this.hasLastDurationSelectTarget) {
      this.lastDurationSelectTarget.value = "full_day"
      this.lastDurationChanged()
    }
  }
}
